import { FirebaseError } from 'firebase/app';
import { User, updateProfile } from 'firebase/auth';
import {
  collection,
  doc,
  documentId,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { MAX_DISPLAY_NAME_LENGTH, PLACEHOLDER_DISPLAY_NAME } from '../utils/accountConstants';
import { hasPasswordProvider, requireCurrentUser, toAuthUser } from './auth.utils';
import { db, functions } from './firebase';

// users/{uid} holds displayName, originalDisplayName and email (see UserData). The email is a
// copy of the Auth email, kept in step on sign-in, so other members' avatars, the share dialog
// and the invite lookup can read it client-side.
const userRef = (uid: string) => doc(db, 'users', uid);

/** Trims and caps a provider-supplied name (e.g. from Google); placeholder when empty. */
const normalizeDisplayName = (name: string | null | undefined): string =>
  name?.trim().slice(0, MAX_DISPLAY_NAME_LENGTH) || PLACEHOLDER_DISPLAY_NAME;

/** Creates the user document, with `displayName` doubling as the permanent original. */
const createUserDocument = async (user: User, displayName: string): Promise<void> => {
  await setDoc(userRef(user.uid), {
    displayName,
    originalDisplayName: displayName,
    createdAt: serverTimestamp(),
    // The rules only accept the caller's own Auth email.
    ...(user.email ? { email: user.email } : {}),
  });
};

/**
 * Called on every auth state change (one read). Creates a missing document, backfills
 * `originalDisplayName` on legacy documents, and keeps `email` in step with Auth (new accounts,
 * and after an email change), writing only when something differs. Returns the
 * display name to show, or `null` when the document was left for email sign-up to create.
 */
export const syncUserDocument = async (user: User): Promise<string | null> => {
  // Read before the await: SignUp's updateProfile mutates this same User object while getDoc
  // is in flight, and checking afterwards would let both paths create the document.
  const authDisplayNameAtStart = user.displayName;
  const snapshot = await getDoc(userRef(user.uid));

  if (!snapshot.exists()) {
    // Mid email sign-up: SignUp creates the document once the name is set. Creating it here
    // too would race that write and could store the placeholder as the original name.
    if (hasPasswordProvider(toAuthUser(user)) && !authDisplayNameAtStart) {
      return null;
    }
    const displayName = normalizeDisplayName(user.displayName);
    await createUserDocument(user, displayName);
    return displayName;
  }

  const data = snapshot.data();
  const displayName = normalizeDisplayName(data.displayName ?? user.displayName);
  const updates: Record<string, string> = {};
  if (data.displayName !== displayName) {
    updates.displayName = displayName;
  }
  if (!data.originalDisplayName) {
    updates.originalDisplayName = displayName;
  }
  if (Object.keys(updates).length > 0) {
    await updateDoc(userRef(user.uid), updates);
  }
  if (user.email && data.email !== user.email) {
    // Best-effort and separate from the name writes: a rejected email write (e.g. rules not yet
    // deployed) must not block sign-in. It retries on the next sign-in.
    await updateDoc(userRef(user.uid), { email: user.email }).catch(() => undefined);
  }
  // Legacy email accounts never had their Auth profile name set.
  if (user.displayName !== displayName) {
    await updateProfile(user, { displayName });
  }
  return displayName;
};

/** Sets the name for a brand-new email/password account: Auth profile, then user document. */
export const completeEmailSignUp = async (user: User, displayName: string): Promise<void> => {
  await updateProfile(user, { displayName });
  await createUserDocument(user, displayName);
};

/** The bio shown on a user's profile page ('' when none is set). */
export const getUserDescription = async (userId: string): Promise<string> => {
  const snapshot = await getDoc(userRef(userId));
  return (snapshot.data()?.description as string | undefined) ?? '';
};

/** Saves the signed-in user's profile bio. The rules cap its length. */
export const updateUserDescription = async (userId: string, description: string) =>
  updateDoc(userRef(userId), { description });

/** User documents (displayName + email) for the given uids. */
export const getUserDocumentsWithIds = async (userIds: string[]) => {
  const q = query(collection(db, 'users'), where(documentId(), 'in', userIds));
  return getDocs(q);
};

/** User documents for the given emails (invite lookup). */
export const getUserDocumentsWithEmails = async (emails: string[]) => {
  const q = query(collection(db, 'users'), where('email', 'in', emails));
  return getDocs(q);
};

/**
 * Updates the signed-in user's display name. Never touches `originalDisplayName`.
 * Firestore is written first so a failure leaves Auth and Firestore in agreement.
 */
export const updateUserDisplayName = async (displayName: string): Promise<void> => {
  const user = requireCurrentUser();
  try {
    await updateDoc(userRef(user.uid), { displayName });
  } catch (error) {
    // A failed email sign-up can leave an account without a document; create it now.
    if (!(error instanceof FirebaseError && error.code === 'not-found')) {
      throw error;
    }
    await createUserDocument(user, displayName);
  }
  await updateProfile(user, { displayName });
};

/**
 * Deactivates the signed-in account via Cloud Function (disables sign-in, revokes sessions,
 * keeps data for 30 days). Reauthenticate first: the function rejects stale logins.
 */
export const requestAccountDeactivation = async (): Promise<void> => {
  await httpsCallable(functions, 'deactivateAccount')();
};
